import type {
  AttachCheckOutput,
  CraRateSet,
  CraSituation,
  RentAssistanceOutput,
} from "./types.js";

// Plain-English CRA copy. Lives in the rules package so every surface (Expo
// result screen, marketing explainer, PDF) renders identical wording from one
// source. This is a regulatory requirement, not just DRY: CRA copy is
// INFORMATION, not advice (validation §3, spec §4.1). We STATE the estimate, how
// it was derived, and the Services Australia source — with NO "you should
// claim", NO "you'll save $X", NO "you're owed", NO product recommendation.
//
// Extra guardrails specific to this module:
//  - CRA is a government supplement → there is no monetisation/affiliate surface
//    and no handoff; copy never routes the user to a financial product.
//  - The attach-checker stays strictly qualitative ("you MAY be able to get
//    CRA — here's the official step"), never a quantified entitlement.
//  - We never assert "many are missing it" as a number (UNVERIFIED). The framing
//    is "a quick check that CRA is switched on for you".

/** Fortnightly AUD, en-AU formatting, always 2dp. */
export function formatAud(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return `A$${rounded.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const SITUATION_LABEL: Record<CraSituation, string> = {
  single_no_children: "single, no children",
  single_sharer_no_children: "single sharer, no children",
  couple_no_children: "couple, no children",
  single_with_children: "single, with children",
  couple_with_children: "couple, with children",
};

export function situationLabel(situation: CraSituation): string {
  return SITUATION_LABEL[situation];
}

/** Headline for the estimate — an estimate figure, NEVER a saving or an owed amount. */
export function estimateHeadline(output: RentAssistanceOutput): string {
  if (output.estimatePerFortnight === 0) {
    return "A$0 — no Commonwealth Rent Assistance is estimated on these figures";
  }
  return `Estimated Commonwealth Rent Assistance on these figures: ${formatAud(
    output.estimatePerFortnight,
  )} per fortnight`;
}

/** Explains WHY the estimate landed where it did (which band is binding). */
export function bandBullet(output: RentAssistanceOutput): string {
  switch (output.band) {
    case "below_threshold":
      return `Your rent of ${formatAud(output.fortnightlyRent)} per fortnight is at or below the ${formatAud(
        output.minRentThreshold,
      )} minimum rent threshold for a ${situationLabel(
        output.situation,
      )} situation, so no Rent Assistance is estimated. Rent would need to be above ${formatAud(
        output.rentToStart,
      )} per fortnight before any Rent Assistance applies.`;
    case "in_taper":
      return `You are in the taper band: Rent Assistance is paid at ${(
        output.taperRate * 100
      ).toFixed(0)} cents for every dollar of rent above the ${formatAud(
        output.minRentThreshold,
      )} minimum threshold. The maximum of ${formatAud(
        output.maxRate,
      )} per fortnight is reached at ${formatAud(output.rentToReachMax)} of rent.`;
    case "at_maximum":
      return `You are at the maximum of ${formatAud(
        output.maxRate,
      )} per fortnight for a ${situationLabel(
        output.situation,
      )} situation. Rent above ${formatAud(
        output.rentToReachMax,
      )} per fortnight does not increase Rent Assistance.`;
  }
}

/** Source-attribution line for the estimate. */
export function estimateSourceBullet(rateSet: CraRateSet): string {
  return `These figures use the Services Australia / DSS Commonwealth Rent Assistance ${rateSet.label} (last verified ${rateSet.sourcesVerifiedAt}). Rent Assistance rates index twice a year (20 March and 20 September) — always check servicesaustralia.gov.au.`;
}

/** The mandatory estimate disclaimer (information, not advice). */
export const ESTIMATE_DISCLAIMER =
  "Educational tool. Not financial advice. This is an estimate only — Services Australia assesses the actual amount. Rent Assistance is payable only while you receive a qualifying payment and have told Services Australia your current rent. Confirm your eligibility at servicesaustralia.gov.au.";

export function estimateExplanation(
  output: RentAssistanceOutput,
  rateSet: CraRateSet,
): string[] {
  return [
    estimateHeadline(output),
    bandBullet(output),
    estimateSourceBullet(rateSet),
    ESTIMATE_DISCLAIMER,
  ];
}

// --- Attach-checker copy (strictly qualitative) -------------------------------

/** The mandatory attach-checker disclaimer. Qualitative, never an entitlement. */
export const ATTACH_DISCLAIMER =
  "Educational tool. Not financial advice. This is a quick check of whether Rent Assistance may apply to your situation — not a Services Australia assessment, and not a statement that you are owed any amount. We are not authorised by Services Australia. Confirm your eligibility at servicesaustralia.gov.au or call 132 850.";

/** Headline verdict — qualitative only; never "you are owed $X". */
export function attachHeadline(output: AttachCheckOutput): string {
  switch (output.verdict) {
    case "may_attach":
      return "Rent Assistance may be able to apply to your situation — here is how to make sure it is switched on.";
    case "cannot_attach":
      return "On these answers, Rent Assistance does not appear able to apply to your situation.";
    case "check_with_services_australia":
      return "Your situation has an edge case — only Services Australia can confirm whether Rent Assistance applies.";
  }
}

/** The action bullets behind the verdict — the "easy to check you're not" steps. */
export function attachBullets(output: AttachCheckOutput): string[] {
  const out: string[] = [];

  if (output.viaFtbPartA) {
    out.push(
      "Rent Assistance can attach to Family Tax Benefit Part A — not only to a Centrelink income-support payment. Many families on FTB Part A don't realise this is a route to Rent Assistance.",
    );
  } else if (output.hasGateway) {
    out.push(
      "You appear to receive a qualifying payment, which is the gateway Rent Assistance attaches to (it is never a standalone payment).",
    );
  }

  if (output.needsToReportRent) {
    out.push(
      "Services Australia only assesses Rent Assistance once it holds your current rent details. If you haven't told them your rent (or it has changed), update it — that is the single most common reason it isn't switched on.",
    );
  }

  if (output.reasons.includes("rent_may_be_below_threshold")) {
    out.push(
      "Even where Rent Assistance can attach, it may work out to A$0 if your rent is at or below the minimum threshold — use the estimator to see which band you're in.",
    );
  }

  if (output.reasons.includes("no_qualifying_payment")) {
    out.push(
      "Rent Assistance is not a standalone payment — it is only paid alongside a qualifying primary payment (a Centrelink pension/allowance or Family Tax Benefit Part A).",
    );
  }
  if (output.reasons.includes("public_housing_excluded")) {
    out.push(
      "Rent Assistance is generally not payable for government-managed public or community housing.",
    );
  }
  if (output.reasons.includes("edge_case_board_lodging")) {
    out.push(
      "Board and lodging arrangements are assessed under special rules — only Services Australia can confirm how Rent Assistance applies to yours.",
    );
  }
  if (output.reasons.includes("edge_case_other_housing")) {
    out.push(
      "Your housing arrangement is non-standard — check with Services Australia whether Rent Assistance can apply.",
    );
  }
  if (output.reasons.includes("payment_status_unsure")) {
    out.push(
      "You weren't sure which payment you receive — check your Centrelink/FTB details, because the qualifying payment is what Rent Assistance attaches to.",
    );
  }

  out.push(ATTACH_DISCLAIMER);
  return out;
}
